import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../Logo/Logo'
import supabase from '../../utils/supabase'

const AuthCallback: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  
  // Check if current language is RTL
  const isRTL = i18n.language === 'he' || i18n.language === 'ar'

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the URL hash parameters
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        const error = hashParams.get('error')
        const errorDescription = hashParams.get('error_description')

        if (error) {
          setError(errorDescription || error)
          setLoading(false)
          return
        }

        if (accessToken && refreshToken) {
          // Set the session with the tokens
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })

          if (sessionError) {
            setError(sessionError.message)
          } else {
            setMessage(t('auth.loginSuccessful'))
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/')
            }, 2000)
          }
        } else {
          // Handle email confirmation or other auth flows
          const { data, error: authError } = await supabase.auth.getSession()
          
          if (authError) {
            setError(authError.message)
          } else if (data.session) {
            setMessage(t('auth.loginSuccessful'))
            setTimeout(() => {
              navigate('/')
            }, 2000)
          } else {
            setError(t('auth.noValidSession'))
          }
        }
      } catch (err) {
        setError(t('auth.unexpectedError'))
      } finally {
        setLoading(false)
      }
    }

    handleAuthCallback()
  }, [navigate, t])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 font-inter" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo size="md" />
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t('auth.processingAuthentication')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 font-inter" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo size="md" />
          </div>
          
          {error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                {t('auth.authenticationError')}
              </h3>
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                {t('auth.returnToLogin')}
              </button>
            </div>
          ) : (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
                {t('auth.authenticationSuccess')}
              </h3>
              <p className="text-sm text-green-600 dark:text-green-400 mb-4">{message}</p>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-xs text-green-500 dark:text-green-400 mt-2">
                {t('auth.redirectingToDashboard')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthCallback
